<template>
  <ColumnChart
    :request-status="requestStatus"
    :categories="formattedCategories(categories)"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import ColumnChart from '@/components/cards/models/ColumnChart';
import channelModel from '@/mixins/channel-model';
import cardsModel from '@/mixins/cards-model';
import { mapActions } from 'vuex';
import EventBus from '@/utils/event-bus';
import { POST_FIELDS, OPERATION, POINTS_PARSER } from '@/services/charts-service';

export default {
  name: 'BaseChannelTimeSeries',
  components: {
    ColumnChart,
  },
  mixins: [
    channelModel,
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    ownerPosts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: {},
      classifiedPercentage: 0,
    };
  },
  computed: {
    categories() {
      return [...new Set(this.rawSeries?.points?.map(({ coordinates }) => coordinates.postedAt))];
    },
    series() {
      const seriesObj = [];

      if (!this.categories?.length) {
        return;
      }

      this.rawSeries?.points?.forEach(({ coordinates, value }) => {
        const channel = coordinates.channel;

        if (!channel) {
          return;
        }

        const { name, color } = this.getChannelColorNameAndIcon(channel);
        const serie = seriesObj.find((serie) => serie.name === name);
        const data = serie?.data || [];

        data[this.categories?.indexOf(coordinates.postedAt)] = value;

        if (serie) {
          return;
        }

        seriesObj.push({
          name,
          color,
          data,
        });
      });

      // normalize undefined as null to render chart correctly
      seriesObj.forEach((serie) => {
        for (let i = 0; i < serie.data.length; i++) {
          serie.data[i] = [this.categories[i], serie.data[i] || null];
        }
      });
      return seriesObj;
    },
    cardOptions() {
      return {
        xAxis: {
          type: 'datetime',
          labels: {
            format: this.labelFormat,
          },
        },
        legend: {
          enabled: true,
        },
        tooltip: {
          xDateFormat: this.xDateFormat,
        },
      };
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        points_parser: POINTS_PARSER.COORDINATES,
        params: {
          grouping: [POST_FIELDS.CHANNEL],
          operators: this.operation,
          sortFields: [POST_FIELDS.POSTED_AT],
          datePortion: this.getDatePortion(options),
        },
      })
        .then(response => {
          this.rawSeries = response;
          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    formattedCategories(categories) {
      return this.isQuarterOnDatePortion
        ? categories.map(x => this.formattedLabelForQuarter(x))
        : categories;
    },
  },
};
</script>
